import { t, tn, useI18n } from '../../i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAward, FiEdit2, FiPlus, FiShield, FiTrash2 } from 'react-icons/fi';
import {
  PERMISSION_CATALOGUE,
  roleService,
  type CompanyRole,
  type RoleKind,
  type StaffAccess,
  type StaffPermission,
} from '../../services/roleService';
import { staffService, type StaffRow } from '../../services/staffService';
import { sanitizeMultilineText, sanitizeText } from '../../utils/sanitize';
import {
  formatHourlyRate,
  roleDescription,
  roleDisplayName,
  roleKindSummary,
} from '../guards/staffPresentation';
import './RolesPage.css';

/**
 * Admin › Roles & Permissions. System Administrators define what each role
 * opens here, set the default pay a guard rank carries, and the System Owner
 * hands the workspace on.
 *
 * Who holds a role is changed on the person's Staff profile, not here: a role
 * is a definition, and this page is where definitions live.
 */

interface Props {
  access: StaffAccess;
}

interface RoleDraft {
  id?: string;
  kind: RoleKind;
  name: string;
  description: string;
  colour: string | null;
  permissions: StaffPermission[];
  hourlyRate: string;
}

const MAX_NAME_LENGTH = 60;
const MAX_DESCRIPTION_LENGTH = 280;

/** Chosen to stay legible as chip text on the workspace's dark surface. */
const ROLE_COLOURS = ['#2dd4bf', '#60a5fa', '#a78bfa', '#f472b6', '#fbbf24', '#f87171', '#4ade80', '#cbd5e1'];

const emptyDraft = (kind: RoleKind): RoleDraft => ({
  kind,
  name: '',
  description: '',
  colour: null,
  permissions: [],
  hourlyRate: '',
});

const draftFor = (role: CompanyRole): RoleDraft => ({
  id: role.id,
  kind: role.kind,
  name: role.name,
  description: role.description ?? '',
  colour: role.colour,
  permissions: [...role.permissions],
  hourlyRate: role.defaultHourlyRate === null ? '' : formatHourlyRate(role.defaultHourlyRate),
});

export default function RolesPage({ access }: Props) {
  useI18n();
  const [roles, setRoles] = useState<CompanyRole[]>([]);
  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [draft, setDraft] = useState<RoleDraft | null>(null);
  const [draftError, setDraftError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<CompanyRole | null>(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [transferError, setTransferError] = useState('');

  const load = useCallback(async () => {
    try {
      const [nextRoles, nextStaff, userId] = await Promise.all([
        roleService.fetchRoles(),
        staffService.fetchStaff(),
        staffService.getCurrentUserId(),
      ]);
      setRoles(nextRoles);
      setStaff(nextStaff);
      setCurrentUserId(userId);
      setError('');
    } catch (err) {
      setError((err as Error).message || t('Could not load roles.'));
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    void load().finally(() => setIsLoading(false));
  }, [load]);

  const managementRoles = useMemo(() => roles.filter((role) => role.kind === 'management'), [roles]);
  const guardRoles = useMemo(() => roles.filter((role) => role.kind === 'guard'), [roles]);
  const owner = useMemo(() => staff.find((member) => member.is_owner) ?? null, [staff]);

  // Ownership can only go to somebody who is already a system administrator
  // and can sign in. The database checks the same thing.
  const transferCandidates = useMemo(
    () => staff.filter((member) => (
      member.role === 'admin' && member.status === 'active' && member.id !== currentUserId
    )),
    [staff, currentUserId],
  );

  const isOwner = access.isOwner || Boolean(owner && owner.id === currentUserId);

  const openEditor = (next: RoleDraft) => {
    setDraft(next);
    setDraftError('');
    setToast('');
  };

  const togglePermission = (permission: StaffPermission) => {
    setDraft((current) => (current ? {
      ...current,
      permissions: current.permissions.includes(permission)
        ? current.permissions.filter((key) => key !== permission)
        : [...current.permissions, permission],
    } : current));
  };

  const saveDraft = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft || isBusy) return;

    const name = sanitizeText(draft.name, MAX_NAME_LENGTH);
    if (!name) {
      setDraftError(t('A role needs a name'));
      return;
    }

    let hourlyRate: number | null = null;
    if (draft.kind === 'guard' && draft.hourlyRate.trim()) {
      hourlyRate = Number(draft.hourlyRate.replace(',', '.'));
      if (!Number.isFinite(hourlyRate) || hourlyRate < 0) {
        setDraftError(t('Enter a valid hourly rate'));
        return;
      }
    }

    setIsBusy(true);
    setDraftError('');
    try {
      await roleService.saveRole({
        id: draft.id,
        name,
        description: sanitizeMultilineText(draft.description, MAX_DESCRIPTION_LENGTH) || null,
        kind: draft.kind,
        permissions: draft.permissions,
        defaultHourlyRate: hourlyRate,
        colour: draft.colour,
      });
      setDraft(null);
      setToast(draft.id
        ? t('{name} has been updated. The change applies the next time its members load the workspace.', { name })
        : t('{name} has been created. Give it to people from their Staff profile.', { name }));
      await load();
    } catch (err) {
      setDraftError((err as Error).message || t('Could not save this role.'));
    } finally {
      setIsBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsBusy(true);
    setError('');
    setToast('');
    try {
      await roleService.deleteRole(deleteTarget.id);
      setToast(t('{name} has been deleted.', { name: roleDisplayName(deleteTarget) }));
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setDeleteTarget(null);
      setError((err as Error).message || t('Could not delete this role.'));
    } finally {
      setIsBusy(false);
    }
  };

  const confirmTransfer = async () => {
    if (!transferTargetId) {
      setTransferError(t('Choose who takes over'));
      return;
    }
    const target = staff.find((member) => member.id === transferTargetId);

    setIsBusy(true);
    setTransferError('');
    try {
      await roleService.transferOwnership(transferTargetId);
      setIsTransferring(false);
      setTransferTargetId('');
      setToast(t('{name} is now the System Owner. You are still a system administrator.', {
        name: target?.full_name || target?.email || t('Unknown'),
      }));
      await load();
    } catch (err) {
      setTransferError((err as Error).message || t('Could not transfer ownership.'));
    } finally {
      setIsBusy(false);
    }
  };

  if (isLoading) return <div className="loading"><div className="spinner" /></div>;

  const renderRoleCard = (role: CompanyRole) => {
    const isAdministratorRole = role.systemKey === 'system_admin';
    const description = roleDescription(role);

    return (
      <article key={role.id} className="card roles-card">
        <header className="roles-card__head">
          <h3 className="roles-card__name">
            <span
              className="roles-card__swatch"
              style={role.colour ? { background: role.colour } : undefined}
              aria-hidden="true"
            />
            {roleDisplayName(role)}
          </h3>
          {role.systemKey && <span className="roles-card__tag">{t('Built-in')}</span>}
        </header>

        {description && <p className="roles-card__description">{description}</p>}

        {role.kind === 'management' ? (
          isAdministratorRole ? (
            <p className="roles-card__all">{t('Every area, including billing, pay rates and roles')}</p>
          ) : role.permissions.length === 0 ? (
            <p className="roles-card__none">{t('No workspace areas yet')}</p>
          ) : (
            <ul className="roles-card__permissions" aria-label={t('Areas this role opens')}>
              {PERMISSION_CATALOGUE
                .filter((permission) => role.permissions.includes(permission.key))
                .map((permission) => (
                  <li key={permission.key} className="roles-card__permission">{t(permission.label)}</li>
                ))}
            </ul>
          )
        ) : (
          <p className={role.defaultHourlyRate === null ? 'roles-card__none' : 'roles-card__pay'}>
            {role.defaultHourlyRate === null
              ? t('No default pay set')
              : t('Default pay {rate} per hour', { rate: formatHourlyRate(role.defaultHourlyRate) })}
          </p>
        )}

        <footer className="roles-card__footer">
          <span className="roles-card__members">
            {tn('{count} person', '{count} people', role.memberCount)}
          </span>
          {!isAdministratorRole && (
            <span className="roles-card__actions">
              <button
                type="button"
                className="btn btn--secondary btn--sm roles-card__action"
                onClick={() => openEditor(draftFor(role))}
                disabled={isBusy}
                aria-label={t('Edit {name}', { name: roleDisplayName(role) })}
              >
                <FiEdit2 aria-hidden="true" />
                {t('Edit')}
              </button>
              {!role.systemKey && (
                <button
                  type="button"
                  className="btn btn--secondary btn--sm roles-card__action"
                  onClick={() => setDeleteTarget(role)}
                  // Moving its members first is the database's rule; saying so
                  // here spares the attempt.
                  disabled={isBusy || role.memberCount > 0}
                  title={role.memberCount > 0 ? t('Move its members to another role before deleting it.') : undefined}
                  aria-label={t('Delete {name}', { name: roleDisplayName(role) })}
                >
                  <FiTrash2 aria-hidden="true" />
                  {t('Delete')}
                </button>
              )}
            </span>
          )}
        </footer>
      </article>
    );
  };

  const renderSection = (kind: RoleKind, title: string, list: CompanyRole[], addLabel: string) => (
    <section className="roles-section" aria-labelledby={`roles-section-${kind}`}>
      <div className="roles-section__head">
        <div>
          <h2 className="roles-section__title" id={`roles-section-${kind}`}>{title}</h2>
          <p className="roles-section__summary">{roleKindSummary(kind)}</p>
        </div>
        <button
          type="button"
          className="btn btn--secondary roles-section__add"
          onClick={() => openEditor(emptyDraft(kind))}
          disabled={isBusy}
        >
          <FiPlus aria-hidden="true" />
          {addLabel}
        </button>
      </div>
      <div className="roles-grid">{list.map(renderRoleCard)}</div>
    </section>
  );

  return (
    <div className="page roles-page">
      <div className="page__header">
        <div>
          <h1 className="page__title">{t('Roles & Permissions')}</h1>
          <p className="page__subtitle">
            {t('Decide what each role can open. Somebody can hold several roles, and gets everything any of them opens.')}
          </p>
        </div>
      </div>

      <div aria-live="polite">
        {toast && <div className="msg--success">{toast}</div>}
      </div>
      {error && <div className="msg--error" role="alert">{error}</div>}

      <section className="card roles-owner" aria-labelledby="roles-owner-title">
        <div className="roles-owner__icon" aria-hidden="true"><FiAward /></div>
        <div className="roles-owner__body">
          <h2 className="roles-owner__title" id="roles-owner-title">{t('System Owner')}</h2>
          <p className="roles-owner__name">
            {owner
              ? owner.full_name || owner.email || t('Unknown')
              : t('Nobody yet')}
            {owner?.email && owner.full_name && <span className="roles-owner__email">{owner.email}</span>}
          </p>
          <p className="roles-owner__text">
            {t('The top administrator of this workspace: the account that created it, until ownership is transferred. If the owner is deactivated, the longest-serving system administrator takes over.')}
          </p>
        </div>
        {isOwner && (
          <div className="roles-owner__action">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => {
                setTransferError('');
                setTransferTargetId('');
                setIsTransferring(true);
              }}
              disabled={isBusy || transferCandidates.length === 0}
            >
              {t('Transfer ownership')}
            </button>
            {transferCandidates.length === 0 && (
              <p className="roles-owner__hint">
                {t('Make somebody a system administrator first.')}
              </p>
            )}
          </div>
        )}
      </section>

      {renderSection('management', t('Management roles'), managementRoles, t('New management role'))}
      {renderSection('guard', t('Guard roles'), guardRoles, t('New guard role'))}

      {draft && (
        <div className="modal-overlay">
          <form
            className="modal roles-editor"
            role="dialog"
            aria-modal="true"
            aria-labelledby="roles-editor-title"
            onSubmit={saveDraft}
            noValidate
          >
            <h2 className="modal__title" id="roles-editor-title">
              {draft.id
                ? t('Edit role')
                : draft.kind === 'guard' ? t('New guard role') : t('New management role')}
            </h2>

            <div aria-live="polite">
              {draftError && <div className="msg--error" role="alert">{draftError}</div>}
            </div>

            <div className="form__group">
              <label className="form__label" htmlFor="roles-editor-name">{t('Role name')}</label>
              <input
                id="roles-editor-name"
                type="text"
                className="form__input"
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                maxLength={MAX_NAME_LENGTH}
                autoComplete="off"
                autoFocus
              />
            </div>

            <div className="form__group">
              <label className="form__label" htmlFor="roles-editor-description">
                {t('Description')} <span className="roles-editor__optional">{t('(optional)')}</span>
              </label>
              <textarea
                id="roles-editor-description"
                className="form__input roles-editor__description"
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                maxLength={MAX_DESCRIPTION_LENGTH}
                rows={2}
              />
            </div>

            <div className="form__group">
              <span className="form__label" id="roles-editor-colour">{t('Colour')}</span>
              <div className="roles-editor__colours" role="group" aria-labelledby="roles-editor-colour">
                <button
                  type="button"
                  className={`roles-editor__colour roles-editor__colour--none${draft.colour === null ? ' roles-editor__colour--selected' : ''}`}
                  aria-pressed={draft.colour === null}
                  aria-label={t('No colour')}
                  onClick={() => setDraft({ ...draft, colour: null })}
                />
                {ROLE_COLOURS.map((colour) => (
                  <button
                    key={colour}
                    type="button"
                    className={`roles-editor__colour${draft.colour === colour ? ' roles-editor__colour--selected' : ''}`}
                    style={{ background: colour }}
                    aria-pressed={draft.colour === colour}
                    aria-label={colour}
                    onClick={() => setDraft({ ...draft, colour })}
                  />
                ))}
              </div>
            </div>

            {draft.kind === 'management' ? (
              <fieldset className="roles-editor__permissions">
                <legend className="form__label">{t('Workspace areas this role opens')}</legend>
                {PERMISSION_CATALOGUE.map((permission) => {
                  const inputId = `roles-editor-permission-${permission.key}`;
                  return (
                    <label key={permission.key} className="roles-editor__permission" htmlFor={inputId}>
                      <input
                        id={inputId}
                        type="checkbox"
                        checked={draft.permissions.includes(permission.key)}
                        onChange={() => togglePermission(permission.key)}
                      />
                      <span className="roles-editor__permission-text">
                        <span className="roles-editor__permission-name">{t(permission.label)}</span>
                        <span className="roles-editor__permission-summary">{t(permission.summary)}</span>
                      </span>
                    </label>
                  );
                })}
                <p className="roles-editor__note">
                  <FiShield aria-hidden="true" />
                  {t('Pay rates, billing, privacy reviews, join codes and roles stay with system administrators.')}
                </p>
              </fieldset>
            ) : (
              <div className="form__group">
                <label className="form__label" htmlFor="roles-editor-rate">
                  {t('Default hourly pay')} <span className="roles-editor__optional">{t('(optional)')}</span>
                </label>
                <input
                  id="roles-editor-rate"
                  type="number"
                  className="form__input roles-editor__rate"
                  value={draft.hourlyRate}
                  onChange={(event) => setDraft({ ...draft, hourlyRate: event.target.value })}
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="0.00"
                />
                <p className="roles-editor__hint">
                  {t('In your payroll currency. Payroll uses it for a guard in this role who has no pay band assigned; a pay band always takes precedence.')}
                </p>
              </div>
            )}

            <div className="roles-editor__footer">
              <button type="button" className="btn btn--secondary" onClick={() => setDraft(null)} disabled={isBusy}>
                {t('Cancel')}
              </button>
              <button type="submit" className="btn btn--primary" disabled={isBusy}>
                {isBusy ? t('Saving...') : draft.id ? t('Save changes') : t('Create role')}
              </button>
            </div>
          </form>
        </div>
      )}

      {deleteTarget && (
        <div className="modal-overlay">
          <div className="modal modal--sm" role="alertdialog" aria-modal="true" aria-labelledby="roles-delete-title">
            <div className="modal__header">
              <h2 className="modal__title" id="roles-delete-title">
                {t('Delete {name}?', { name: roleDisplayName(deleteTarget) })}
              </h2>
            </div>
            <p className="text-muted roles-confirm__text">
              {t('Nobody holds this role, so nobody loses access. This cannot be undone.')}
            </p>
            <div className="roles-confirm__actions">
              <button type="button" className="btn btn--secondary" onClick={() => setDeleteTarget(null)} disabled={isBusy}>
                {t('Cancel')}
              </button>
              <button type="button" className="btn btn--danger" onClick={() => { void confirmDelete(); }} disabled={isBusy}>
                {isBusy ? t('Deleting...') : t('Delete role')}
              </button>
            </div>
          </div>
        </div>
      )}

      {isTransferring && (
        <div className="modal-overlay">
          <div className="modal modal--sm" role="alertdialog" aria-modal="true" aria-labelledby="roles-transfer-title">
            <div className="modal__header">
              <h2 className="modal__title" id="roles-transfer-title">{t('Transfer ownership')}</h2>
            </div>
            <p className="text-muted roles-confirm__text">
              {t('The new owner becomes the top administrator of this workspace. You stay a system administrator, and only they can transfer ownership back.')}
            </p>

            <div aria-live="polite">
              {transferError && <div className="msg--error" role="alert">{transferError}</div>}
            </div>

            <div className="form__group">
              <label className="form__label" htmlFor="roles-transfer-target">{t('New System Owner')}</label>
              <select
                id="roles-transfer-target"
                className="form__select"
                value={transferTargetId}
                onChange={(event) => setTransferTargetId(event.target.value)}
                disabled={isBusy}
              >
                <option value="">{t('Choose a system administrator')}</option>
                {transferCandidates.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.full_name || member.email || t('Unknown')}
                  </option>
                ))}
              </select>
            </div>

            <div className="roles-confirm__actions">
              <button type="button" className="btn btn--secondary" onClick={() => setIsTransferring(false)} disabled={isBusy}>
                {t('Cancel')}
              </button>
              <button type="button" className="btn btn--danger" onClick={() => { void confirmTransfer(); }} disabled={isBusy}>
                {isBusy ? t('Transferring...') : t('Transfer ownership')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
